import {
  notificationListResponseSchema,
  notificationPreferenceListSchema,
  notificationReadAllResponseSchema,
  notificationSchema,
  notificationUnreadCountSchema,
  type Notification,
  type NotificationCategory,
  type NotificationListResponse,
  type NotificationPreferenceValue,
  type NotificationSeverity,
  type NotificationUnreadCount,
} from '@erp/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { apiFetch, type ApiError } from '@/lib/api-client';

export interface NotificationListParams {
  page: number;
  pageSize: number;
  category?: NotificationCategory | undefined;
  severity?: NotificationSeverity | undefined;
  unreadOnly: boolean;
}

export const notificationKeys = {
  all: ['notifications'] as const,
  list: (params: NotificationListParams) => ['notifications', 'list', params] as const,
  unread: ['notifications', 'unread-count'] as const,
  preferences: ['notifications', 'preferences'] as const,
};

function toSearchParams(params: NotificationListParams): string {
  const search = new URLSearchParams({
    page: String(params.page),
    pageSize: String(params.pageSize),
    unreadOnly: String(params.unreadOnly),
  });

  if (params.category !== undefined) {
    search.set('category', params.category);
  }
  if (params.severity !== undefined) {
    search.set('severity', params.severity);
  }

  return search.toString();
}

export function useNotifications(
  params: NotificationListParams,
  enabled = true,
): UseQueryResult<NotificationListResponse, ApiError> {
  return useQuery({
    queryKey: notificationKeys.list(params),
    queryFn: () =>
      apiFetch(`/api/v1/notifications?${toSearchParams(params)}`, notificationListResponseSchema),
    placeholderData: (previous) => previous,
    enabled,
  });
}

export function useUnreadCount(): UseQueryResult<NotificationUnreadCount, ApiError> {
  return useQuery({
    queryKey: notificationKeys.unread,
    queryFn: () => apiFetch('/api/v1/notifications/unread-count', notificationUnreadCountSchema),
    // Der SSE-Stream aktualisiert den Zähler; das Intervall dient nur als Rückfallebene.
    refetchInterval: 5 * 60_000,
  });
}

export function useMarkNotificationRead(): UseMutationResult<Notification, ApiError, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/api/v1/notifications/${id}/read`, notificationSchema, { method: 'PATCH' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

export function useMarkAllNotificationsRead(): UseMutationResult<
  { markedRead: number },
  ApiError,
  void
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      apiFetch('/api/v1/notifications/read-all', notificationReadAllResponseSchema, {
        method: 'POST',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

export function useNotificationPreferences(): UseQueryResult<
  NotificationPreferenceValue[],
  ApiError
> {
  return useQuery({
    queryKey: notificationKeys.preferences,
    queryFn: async () => {
      const response = await apiFetch(
        '/api/v1/notifications/preferences',
        notificationPreferenceListSchema,
      );
      return response.data;
    },
  });
}

export function useUpdateNotificationPreferences(): UseMutationResult<
  NotificationPreferenceValue[],
  ApiError,
  NotificationPreferenceValue[],
  { previous: NotificationPreferenceValue[] | undefined }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (preferences: NotificationPreferenceValue[]) => {
      const response = await apiFetch(
        '/api/v1/notifications/preferences',
        notificationPreferenceListSchema,
        { method: 'PUT', body: { preferences } },
      );
      return response.data;
    },
    // Die Schalter reagieren sofort; bei einem Fehler wird der alte Stand zurückgesetzt.
    onMutate: async (preferences) => {
      await queryClient.cancelQueries({ queryKey: notificationKeys.preferences });
      const previous = queryClient.getQueryData<NotificationPreferenceValue[]>(
        notificationKeys.preferences,
      );

      if (previous !== undefined) {
        const changed = new Map(preferences.map((entry) => [entry.category, entry]));
        queryClient.setQueryData<NotificationPreferenceValue[]>(
          notificationKeys.preferences,
          previous.map((entry) => changed.get(entry.category) ?? entry),
        );
      }

      return { previous };
    },
    onError: (_error, _preferences, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(notificationKeys.preferences, context.previous);
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData(notificationKeys.preferences, data);
    },
  });
}
